import React, { useRef, useEffect, useCallback, useContext, useState, memo } from 'react'
import { useSelector } from 'react-redux'
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom'
import PropTypes from 'prop-types'
import cx from 'classnames'
import * as PT from '~/PropTypes'

import { useDOMEvent, useLatestRef, useAction } from '~/hooks'
import { pixel2Time } from '~/Util'
import TimelineRuller from './TimeLineRuller'
import SliderPointer from './SliderPointer'
import { TimelineScrollPositionContext } from './ScrollPositionContext'
import * as Actions from '~/actions'

import './TimelineHeader.scss'
import { IOPointsSlider } from './IoPoints/IOPointsSlider'
import { TimelineHeaderContextMenu } from './TimelineHeaderContextMenu'
import { TIMELINE_DATASET_IDS } from '~/config/constants/timeline'
import { MARK_TYPE } from '~/config/constants/preview'
import { useTimelineMarkerCommands } from '~/hooks/timeline/iopoints/useTimelineMarkerCommands'
import { togglePlaybackPlaying } from '~/actions/playback'
import { PLAYER_TYPE, PLAYBACK_STATE } from '~/enums'
import { selectIsTimelinePlaying } from '~/selectors/playback'
import { selectViewSettings } from '~/selectors/mainView'
import { ViewSettings } from '~/config/constants/editorSettings'
import { SliderTimeContext } from '~/contexts/SliderTimeContext'

function TimelineHeader(props) {
  const {
    scale,
    disabled,
    scrollbarRef,
  } = props

  const isPlaying = useSelector(selectIsTimelinePlaying)
  const viewSettings = useSelector(selectViewSettings)
  const {
    refIsMouseDown,
    setSliderSticky,
  } = useContext(TimelineScrollPositionContext)
  const { setLocalTime } = useContext(SliderTimeContext)

  const [ anchorEl, setAnchorEl ] = useState(null)
  const [ anchorPosition, setAnchorPosition ] = useState({ top: 0, left: 0 })

  const refRuller = useRef()
  const memoPlayback = useRef(null)
  const saveTimeout = useRef(null)
  const refDocument = useRef(document)

  const saveProject = useAction(Actions.saveProject.saveProject)
  const setIsMovingSlider = useAction(Actions.timeline.setIsMovingSlider)
  const togglePlayback = useAction(togglePlaybackPlaying)
  const timelinePlaybackState = useSelector(state => state.playback.timelinePlaybackState)

  const { onSetMarker, onClearInOutPoints: onClearTimelineMarkers } = useTimelineMarkerCommands()

  const onCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const onOpenMenu = useCallback(e => {
    e.preventDefault()
    if (disabled || !viewSettings[ViewSettings.showInOutPointsTimeline]) return
    setAnchorPosition({ top: e.clientY, left: e.clientX })
    setAnchorEl(refRuller.current)
  }, [ disabled, viewSettings ])

  const onMarkIn = useCallback(() => {
    onSetMarker(MARK_TYPE.IN)
    setAnchorEl(null)
  }, [ onSetMarker ])

  const onMarkOut = useCallback(() => {
    onSetMarker(MARK_TYPE.OUT)
    setAnchorEl(null)
  }, [ onSetMarker ])

  const onClearInOutPoints = useCallback(() => {
    onClearTimelineMarkers()
    setAnchorEl(null)
  }, [ onClearTimelineMarkers ])

  const scrollContext = useContext(TimelineScrollPositionContext)
  const scrollRef = useLatestRef(scrollContext)

  useEffect(() => () => {
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current)
    }
  }, [])

  const getTimeFromMouseEvent = useCallback(
    event => {
      const $ruller = refRuller.current
      if (!$ruller) return 0

      const rect = $ruller.getBoundingClientRect()
      const left = rect.left - $ruller.offsetLeft
      const position = event.clientX - $ruller.offsetLeft - left + scrollRef.current.scrollLeft

      return Math.max(0, pixel2Time(position, scale))
    },
    [ scale, scrollRef ]
  )

  const onMouseDown = useCallback(
    e => {
      if (disabled || refIsMouseDown.current || e.button !== 0) {
        return
      }
      e.preventDefault()

      refIsMouseDown.current = true
      setSliderSticky(false)
      memoPlayback.current = timelinePlaybackState

      togglePlayback({
        playbackState: PLAYBACK_STATE.PAUSE,
        playerType: PLAYER_TYPE.TIMELINE,
      })

      setLocalTime(getTimeFromMouseEvent(e), { isRewind: true })
    },
    [
      disabled,
      refIsMouseDown,
      setSliderSticky,
      timelinePlaybackState,
      togglePlayback,
      setLocalTime,
      getTimeFromMouseEvent,
    ]
  )

  const onMouseUp = useCallback(
    () => {
      if (!refIsMouseDown.current) {
        return
      }
      batchedUpdates(() => {
        refIsMouseDown.current = false

        togglePlayback({
          playbackState: memoPlayback.current || PLAYBACK_STATE.PAUSE,
          playerType: PLAYER_TYPE.TIMELINE,
        })

        memoPlayback.current = null
        setIsMovingSlider(false)
      })

      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current)
      }

      saveTimeout.current = setTimeout(() => {
        saveProject()
      }, 500)
    },
    [ refIsMouseDown, togglePlayback, setIsMovingSlider, saveProject ]
  )

  const onMouseMove = useCallback(
    e => {
      if (!refIsMouseDown.current) {
        return
      }
      e.preventDefault()

      batchedUpdates(() => {
        setIsMovingSlider(true)
        setLocalTime(getTimeFromMouseEvent(e), { isRewind: true })
      })
    },
    [ refIsMouseDown, setIsMovingSlider, setLocalTime, getTimeFromMouseEvent ]
  )

  useDOMEvent(refDocument, 'mousemove', onMouseMove)
  useDOMEvent(refDocument, 'mouseup', onMouseUp)

  return (
    <div
      className={cx('timeline-header', { 'timeline-header--disabled': disabled })}
      data-id={TIMELINE_DATASET_IDS.HEADER}
      onContextMenu={onOpenMenu}
    >
      <div
        className="timeline-header__ruller"
        ref={refRuller}
        onMouseDown={onMouseDown}
      >
        <IOPointsSlider
          scale={scale}
          getTimeFromMouseEvent={getTimeFromMouseEvent}
          scrollbarRef={scrollbarRef}
        />
        <TimelineRuller />
        <SliderPointer
          scale={scale}
          refRuller={refRuller}
          isPlaying={isPlaying}
        />
      </div>
      <TimelineHeaderContextMenu
        anchorEl={anchorEl}
        anchorPosition={anchorPosition}
        onCloseMenu={onCloseMenu}
        onMarkIn={onMarkIn}
        onMarkOut={onMarkOut}
        onClearInOutPoints={onClearInOutPoints}
      />
    </div>
  )
}

TimelineHeader.defaultProps = {
  disabled: false,
}

TimelineHeader.propTypes = {
  scale: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  scrollbarRef: PT.RefObject.isRequired,
}

export default memo(TimelineHeader)
